import { useEffect, useMemo, useState } from "react"

import { Layer } from "../../components/Layer"
import { SELECTED_LINE_LAYER_SIZE, USER_LOCATION_ACCURACY_LAYER, USER_LOCATION_LAYER, USER_LOCATION_SOURCE } from "../../constants/layers"
import { useMapDispatchContext, useMapStateContext } from "../../contexts/MapContext"
import { useOrderingLayers } from "../../hooks/useOrderingLayers"
import { getInterpolatedLineLayerWidth, getPointCircleLayerConfig } from "../../utils/layersConfigs"

import { UserLocationSource } from "../UserLocationSource"

export function UserLocationLayer() {
    const { getMapRef } = useMapDispatchContext()
    const { userLocation } = useMapStateContext()
    const [zoom, setZoom] = useState(() => getMapRef()?.getZoom() || 0)

    useEffect(() => {
        const mapRef = getMapRef()
        if (!mapRef) {
            return
        }

        const map = mapRef.getMap()
        if (!map) {
            return
        }

        const handleZoomEnd = () => {
            setZoom(map.getZoom())
        }

        map.on("zoomend", handleZoomEnd)
        return () => {
            map.off("zoomend", handleZoomEnd)
        }
    }, [getMapRef])

    const layerConfig = useMemo(() => {
        return getPointCircleLayerConfig({
            "paint": {
                "circle-color": "#0288D1",
                "circle-radius": getInterpolatedLineLayerWidth(SELECTED_LINE_LAYER_SIZE / 2),
                "circle-stroke-color": "#FFFFFF",
                "circle-stroke-width": 2,
            },
        })
    }, [])

    const accuracyLayerConfig = useMemo(() => {
        if (!userLocation) {
            return null
        }

        const earthCircumference = 40075016.686;
        const metersPerPixel =
            (earthCircumference * Math.cos(userLocation.latitude * Math.PI / 180)) /
            Math.pow(2, zoom + 9);

        const radius = userLocation.accuracy / metersPerPixel;

        return getPointCircleLayerConfig({
            "paint": {
                "circle-color": "#0289d1",
                "circle-opacity": 0.2,
                "circle-radius": radius,
                "circle-stroke-color": "#0289d1",
                "circle-stroke-opacity": 0.3,
                "circle-stroke-width": 1,
            },
        })
    }, [zoom, userLocation])

    useOrderingLayers([USER_LOCATION_LAYER])

    return (
        <UserLocationSource
            id={USER_LOCATION_SOURCE}
            tolerance={.001}
            type="geojson"
        >
            <Layer
                id={USER_LOCATION_LAYER}
                {...layerConfig}
            />
            <Layer
                id={USER_LOCATION_ACCURACY_LAYER}
                {...accuracyLayerConfig}
            />
        </UserLocationSource>
    )
}